import type { CloseCalendarItem, CloseCalendarPeriod, CloseCalendarRule, CloseCalendarRuleCondition } from './types'
import type { Badge } from '../../components/ui'
import type React from 'react'

export const APPLET_DISPLAY_NAMES: Record<string, string> = {
  ap_cutoff: 'AP Cut-Off',
  payroll_entry: 'Payroll Entry',
  payroll_accruals: 'Payroll Accruals',
  bank_reconciliation: 'Bank Reconciliation',
  lag_accruals: 'Lag Accruals',
  judgmental_accruals: 'Judgmental Accruals',
  asc_606_revenue_waterfall: 'ASC 606 Revenue Waterfall',
  deferred_revenue_scheduler: 'Deferred Revenue Scheduler',
  subledger_revenue_reconciler: 'Sub-Ledger Revenue Reconciler',
  cloud_accruals: 'Cloud Accruals',
  variable_lag_accruals: 'Variable Lag Accruals',
}

export function getCloseCalendarStatusVariant(status: string): React.ComponentProps<typeof Badge>['variant'] {
  if (['complete', 'approved', 'closed', 'overridden'].includes(status)) return 'success'
  if (status === 'ready') return 'default'
  if (status === 'blocked') return 'danger'
  if (status === 'needs_review') return 'warning'
  return 'secondary'
}

export function getCloseCalendarCompletionSummary(period?: CloseCalendarPeriod) {
  const items = period?.items ?? []
  const complete = items.filter(i => ['complete', 'approved', 'closed', 'overridden'].includes(i.status)).length
  const blocked = items.filter(i => i.status === 'blocked').length
  const needsReview = items.filter(i => i.status === 'needs_review').length
  return { total: items.length, complete, blocked, needsReview }
}

export function formatStatus(status: string) {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function resolveActionLabel(item: CloseCalendarItem): string {
  const applet = item.owning_applet_key ?? ''
  const name = APPLET_DISPLAY_NAMES[applet] ?? item.name
  const { status } = item
  if (status === 'needs_review') return `Approve ${name}`
  if (status === 'ready') {
    if (applet === 'bank_reconciliation') return 'Start Reconciliation'
    if (applet === 'judgmental_accruals') return 'Enter Accruals'
    return `Run ${name}`
  }
  if (status === 'running') return 'View Progress'
  if (['complete', 'approved', 'closed', 'overridden'].includes(status)) return `View ${name}`
  return `Open ${name}`
}

const OPERATOR_LABELS: Record<string, string> = {
  equals: 'is',
  not_equals: 'is not',
  in: 'is one of',
  not_in: 'is not one of',
  gt: 'is greater than',
  lt: 'is less than',
  gte: 'is at least',
  lte: 'is at most',
}

export type CloseCalendarPipelineStep = {
  key: string
  label: string
  sequenceOrder: number
}

export function humanizeCloseCalendarKey(key: string) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function buildCloseCalendarPipelineSteps(
  items: CloseCalendarItem[] = [],
  rules: CloseCalendarRule[] = [],
): CloseCalendarPipelineStep[] {
  if (items.length > 0) {
    return [...items]
      .sort((a, b) => a.sequence_order - b.sequence_order)
      .map(item => ({
        key: item.item_key,
        label: item.name,
        sequenceOrder: item.sequence_order,
      }))
  }

  const seen = new Map<string, number>()
  const dependencies = new Map<string, Set<string>>()
  const addKey = (key: string) => {
    if (!seen.has(key)) {
      seen.set(key, seen.size)
    }
  }

  rules.forEach(rule => {
    addKey(rule.target_item_key)
    rule.conditions
      ?.filter(condition => condition.source_type === 'close_item_status')
      .forEach(condition => {
        const sourceKey = condition.source_item_key
        if (sourceKey) {
          addKey(sourceKey)
          const targetDependencies = dependencies.get(rule.target_item_key) ?? new Set<string>()
          targetDependencies.add(sourceKey)
          dependencies.set(rule.target_item_key, targetDependencies)
        }
      })
  })

  const ordered: string[] = []
  const assigned = new Set<string>()
  let remaining = Array.from(seen.keys())
  while (remaining.length > 0) {
    const stage = remaining.filter(key => {
      const requiredDependencies = Array.from(dependencies.get(key) ?? new Set<string>())
      return requiredDependencies.every(dep => assigned.has(dep) || !seen.has(dep))
    })
    const next = stage.length > 0 ? stage : remaining
    next
      .sort((a, b) => (seen.get(a) ?? 0) - (seen.get(b) ?? 0))
      .forEach(key => {
        ordered.push(key)
        assigned.add(key)
      })
    remaining = remaining.filter(key => !assigned.has(key))
  }

  return ordered.map((key, index) => ({
    key,
    label: humanizeCloseCalendarKey(key),
    sequenceOrder: index + 1,
  }))
}

export function conditionToHuman(condition: CloseCalendarRuleCondition): string {
  const sourceKey = condition.source_item_key ?? condition.source_applet_key ?? ''
  const sourceName = APPLET_DISPLAY_NAMES[sourceKey] ?? sourceKey.replace(/_/g, ' ')
  const op = OPERATOR_LABELS[condition.operator] ?? condition.operator
  const value = condition.expected_value.replace(/_/g, ' ')
  return `${sourceName} ${op} ${value}`
}

export function computePipelineStages(items: CloseCalendarItem[]): CloseCalendarItem[][] {
  const stages: CloseCalendarItem[][] = []
  const assigned = new Set<string>()
  let remaining = [...items]
  while (remaining.length > 0) {
    const stage = remaining.filter(item => item.dependencies.every(dep => assigned.has(dep)))
    if (stage.length === 0) break
    stages.push(stage)
    stage.forEach(item => assigned.add(item.item_key))
    remaining = remaining.filter(item => !assigned.has(item.item_key))
  }
  return stages
}
